import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useCms } from "./CmsContext";
import { getCollection } from "./cmsUtils";

export function PublishModal({ onClose }) {
  const { api, API_BASE, publishStatus, changedCount, refreshPublishStatus, config } = useCms();
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState(null);

  const publish = async () => {
    setPublishing(true);
    const res = await api.post(`${API_BASE}/publish`, {}, null, { timeout: 60000 });
    setPublishing(false);
    if (res && res.success) {
      setResult(res);
      refreshPublishStatus();
    } else {
      toast.error(res?.error || "Publish failed");
    }
  };

  const entryLabel = (item) => {
    const [collectionKey] = String(item.targetKey || "").split(":");
    const collection = getCollection(collectionKey);
    return collection ? `${collection.singular}: ${item.targetLabel || item.slug}` : item.targetLabel;
  };

  return (
    <div className="lw-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lw-modal__panel">
        <div className="lw-modal__head">
          <h2 className="lw-modal__title">{result ? "Published" : "Publish to the live site"}</h2>
          <button type="button" className="lw-iconbtn" title="Close" onClick={onClose}>
            ×
          </button>
        </div>

        {!result ? (
          <>
            <div className="lw-modal__body">
              {changedCount === 0 ? (
                <p className="lw-empty">
                  Everything is up to date — there are no unpublished changes. Publishing will still
                  trigger a site rebuild.
                </p>
              ) : (
                <>
                  <p className="lw-modal__lede">
                    {changedCount} item{changedCount === 1 ? "" : "s"} will go live:
                  </p>
                  <ul className="lw-changelist">
                    {publishStatus.changedDocuments.map((d) => (
                      <li key={d.key} className="lw-changelist__item">
                        <span className="lw-badge lw-badge--page">Page</span>
                        {d.label || d.key}
                        {d.updatedBy?.fullName && (
                          <span className="lw-changelist__by">edited by {d.updatedBy.fullName}</span>
                        )}
                      </li>
                    ))}
                    {publishStatus.changedEntries.map((e) => (
                      <li key={`${e.collectionKey}:${e.slug}`} className="lw-changelist__item">
                        <span className={`lw-badge ${e.deleted ? "lw-badge--deleted" : "lw-badge--entry"}`}>
                          {e.deleted ? "Removing" : e.status === "draft" ? "New" : "Updated"}
                        </span>
                        {entryLabel({ targetKey: `${e.collectionKey}:${e.slug}`, targetLabel: e.label })}
                        {e.updatedBy?.fullName && (
                          <span className="lw-changelist__by">edited by {e.updatedBy.fullName}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {!config.deployHookConfigured && (
                <p className="lw-note lw-note--warn">
                  The Vercel deploy hook isn’t configured on the server yet, so content will be marked
                  as published but the site won’t rebuild automatically.
                </p>
              )}
            </div>
            <div className="lw-modal__foot">
              <button type="button" className="lw-btn lw-btn--ghost" onClick={onClose}>
                Cancel
              </button>
              <button type="button" className="lw-btn lw-btn--primary" disabled={publishing} onClick={publish}>
                {publishing ? "Publishing…" : "Publish now"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="lw-modal__body">
              <p className="lw-modal__lede">
                {result.changedItems.length} item{result.changedItems.length === 1 ? "" : "s"} published.
              </p>
              {result.release?.deployHookStatus === "triggered" && (
                <p className="lw-note lw-note--ok">
                  Site rebuild triggered — changes typically go live within a couple of minutes.
                </p>
              )}
              {result.release?.deployHookStatus === "skipped" && (
                <p className="lw-note lw-note--warn">
                  Deploy hook not configured — the site will pick up these changes on its next build.
                </p>
              )}
              {result.release?.deployHookStatus === "failed" && (
                <p className="lw-note lw-note--error">
                  The deploy hook call failed. Content is published; trigger a rebuild manually from
                  Vercel, or publish again to retry.
                </p>
              )}
            </div>
            <div className="lw-modal__foot">
              <button type="button" className="lw-btn lw-btn--primary" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
